/**
 * Matching panel embedded in JobDetailPage.
 * Hosts two sub-views: History (matrix) and Create (3-panel selector).
 */

import { useMemo } from 'react';
import { useAuth } from '@aaas/shared';
import { useTriggerAnalysisMutation } from '../../hooks/useAnalysisQueries';
import { useAllJDListQuery } from '../../hooks/useJobDescriptionQueries';
import { MatchingHistoryView } from './MatchingHistoryView';
import { MatchingCreateView } from './MatchingCreateView';
import type { JobCVCard } from '../../types';

type MatchingSubView = 'history' | 'create';

interface Props {
  jobId: string;
  cvCards: JobCVCard[];
  isCVsLoading: boolean;
  subView: MatchingSubView;
}

export function JobMatchingPanel({
  jobId,
  cvCards,
  isCVsLoading,
  subView,
}: Props) {
  const { currentWorkspace, idToken } = useAuth();

  // JDs for this job (for create panel)
  const { data: jdsData, isLoading: isJDsLoading } = useAllJDListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    jobIds: [jobId],
    enabled: subView === 'create',
    refetchInterval: false,
  });

  const triggerMutation = useTriggerAnalysisMutation(jobId);

  const jds = jdsData?.data?.items ?? [];

  // Flatten all CVs from kanban columns, deduplicated
  const allCVCards = useMemo(() => {
    const seen = new Set<string>();
    return cvCards.filter((cv) => {
      if (seen.has(cv.id)) return false;
      seen.add(cv.id);
      return true;
    });
  }, [cvCards]);

  return (
    <div className="flex flex-col h-full">
      {/* Content */}
      <div className="flex-1 min-h-0">
        {subView === 'history' ? (
          <MatchingHistoryView
            jobId={jobId}
            workspaceId={currentWorkspace?.id}
            idToken={idToken || undefined}
          />
        ) : (
          <MatchingCreateView
            jobId={jobId}
            jds={jds}
            cvCards={allCVCards}
            isJDsLoading={isJDsLoading}
            isCVsLoading={isCVsLoading}
            onTrigger={(cvId, jdId) => triggerMutation.mutateAsync({ cvId, jdId })}
          />
        )}
      </div>
    </div>
  );
}
